import { remountFrontEndSubtree } from "@/main/bootstrap/bootstrapFrontEnd";
import { applyPreMountDocumentEffectsFromDocument } from "@/main/navigation/NavigationDocumentEffects";
import { commitShellStateFromDocument } from "@/main/navigation/ShellStateStore";
import { unmountReactTreeWithin } from "@/main/react/mounting";
import { syncHeadFromDocument } from "@/main/navigation/HeadSync";
import { resolveMessageCatalogKey } from "@/main/navigation/MessageCatalog";
import type { DocumentSwapResult } from "@/main/navigation/NavigationTypes";

const NAVIGATION_ROOT_SELECTOR = '[data-navigation-root="main-content"]';

/**
 * A soft swap was refused because the incoming document needs a different chrome-message catalog.
 *
 * This is an EXPECTED outcome, not a failure: the `messages_xx.js` module for the current language has
 * already executed and cannot be un-executed, so a genuine language change must hard-navigate. It is a
 * distinct type so `EnhancedNavigation` can log it at `info` while real swap failures (a missing
 * navigation root) stay at `warn` — previously both were bare `Error`s and indistinguishable in prod
 * logs, which buried real failures under the expected ones.
 */
export class CrossLocaleNavigationUnsupportedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "CrossLocaleNavigationUnsupportedError";
    }
}

function syncElementAttributes(currentElement: HTMLElement, nextElement: HTMLElement): void {
    Array.from(currentElement.attributes).forEach((attribute) => {
        currentElement.removeAttribute(attribute.name);
    });

    Array.from(nextElement.attributes).forEach((attribute) => {
        currentElement.setAttribute(attribute.name, attribute.value);
    });
}

export function swapDocumentContent(nextDocument: Document): DocumentSwapResult {
    const currentRoot = document.querySelector(NAVIGATION_ROOT_SELECTOR) as HTMLElement | null;
    const nextRoot = nextDocument.querySelector(NAVIGATION_ROOT_SELECTOR) as HTMLElement | null;

    if (!currentRoot) {
        throw new Error("Enhanced navigation could not find the current navigation root.");
    }

    if (!nextRoot) {
        throw new Error("Enhanced navigation could not find the next navigation root.");
    }

    // Compare the resolved MESSAGE CATALOG, not the raw tag. `en` and `en-US` (and `pt`/`pt-BR`) load
    // the identical messages_xx.js bundle, so swapping between them is provably as safe as a same-tag
    // swap — while a raw-string compare hard-reloaded users whose cookie tag and rendered tag had merely
    // drifted in FORMAT, with no language change at all. Genuinely different languages still refuse.
    // See designs/01-now/2026-07-24-cross-locale-navigation-hard-reload-design.md (cause 2, option C).
    const nextLang = nextDocument.documentElement.lang?.trim();
    const currentLang = document.documentElement.lang.trim();

    if (nextLang) {
        const nextCatalog = resolveMessageCatalogKey(nextLang);
        const currentCatalog = resolveMessageCatalogKey(currentLang);

        if (nextCatalog !== currentCatalog) {
            // Both the raw tags and the resolved catalogs are reported so a prod log line alone
            // distinguishes a genuine language change from format drift, without a code read.
            throw new CrossLocaleNavigationUnsupportedError(
                "Enhanced navigation does not yet support cross-locale document swaps. "
                + `(current=${currentLang || "<empty>"}, next=${nextLang}, `
                + `currentCatalog=${currentCatalog}, nextCatalog=${nextCatalog})`,
            );
        }
    }

    unmountReactTreeWithin(currentRoot);
    syncElementAttributes(currentRoot, nextRoot);
    currentRoot.innerHTML = nextRoot.innerHTML;
    syncHeadFromDocument(nextDocument);
    applyPreMountDocumentEffectsFromDocument(nextDocument);
    commitShellStateFromDocument(nextDocument);
    remountFrontEndSubtree(currentRoot);

    return {
        root: currentRoot,
    };
}
